import { useMemo } from 'react';

import type { DraftWorkItem } from '../controllers/use-ai-create-project-context/types';

import { useStableArray } from './useStableArray';

/**
 * Builds a set of unique field IDs from a collection of draft work items,
 * including all nested child work items recursively.
 *
 * Traverses the provided `draftWorkItems` array in a depth-first manner,
 * collecting all field keys from each work item and its descendants.
 *
 * @param draftWorkItems - An array of draft work items to process.
 * @returns A `Set<string>` containing all unique field IDs found in the work items and their children.
 */
function toUniqueFieldIds(draftWorkItems: readonly DraftWorkItem[]): Set<string> {
	const unique = new Set<string>();
	if (!draftWorkItems.length) {
		return unique;
	}

	// Use a stack to process items depth-first; order is irrelevant for a Set.
	// Spread the readonly array into a mutable stack for depth-first processing.
	const stack: DraftWorkItem[] = [...draftWorkItems];

	while (stack.length) {
		const current = stack.pop();
		// current is expected to be defined because we only push non-null items.
		if (current) {
			const { fields, childWorkItems } = current;
			if (fields) {
				for (const key of Object.keys(fields)) {
					unique.add(key);
				}
			}
			if (childWorkItems && childWorkItems.length) {
				for (let i = 0; i < childWorkItems.length; i++) {
					const child = childWorkItems[i];
					if (child) {
						stack.push(child);
					}
				}
			}
		}
	}

	return unique;
}

/**
 * React hook that computes a stable set of unique field IDs from an array of draft work items.
 *
 * @param draftWorkItems - An array of draft work items to extract unique field IDs from.
 * @returns An object containing the stable set of unique field IDs (`uniqueFieldIds`).
 */
export function useUniqueSuggestedField(draftWorkItems: readonly DraftWorkItem[]): Set<string> {
	const uniqueFieldIds = useStableArray([...toUniqueFieldIds(draftWorkItems)], {
		sort: true,
		deduplicate: false,
	});

	return useMemo(() => {
		return new Set(uniqueFieldIds);
	}, [uniqueFieldIds]);
}
